"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.commentPerspectiveTagSchema = exports.commentPerspectiveInputSchema = exports.MAX_PERSPECTIVE_LABEL_LENGTH = exports.MAX_COMMENT_PERSPECTIVES = exports.WRITE_IN_PERSPECTIVE_KEY = exports.MAX_CUSTOM_PERSPECTIVE_LENGTH = void 0;
exports.parseFirstEmojiFromString = parseFirstEmojiFromString;
exports.parsePerspectiveLines = parsePerspectiveLines;
exports.perspectiveVoteLabel = perspectiveVoteLabel;
exports.formatPerspectiveLines = formatPerspectiveLines;
exports.hasLeadingEmoji = hasLeadingEmoji;
exports.sanitizePerspectiveLabel = sanitizePerspectiveLabel;
exports.containsLetterOrEmoji = containsLetterOrEmoji;
exports.containsEmoji = containsEmoji;
exports.validateWriteInPerspectiveLabel = validateWriteInPerspectiveLabel;
exports.perspectiveTagsToInput = perspectiveTagsToInput;
/**
 * Converts a space's perspective list between its stored form and its entries.
 *
 * The list is stored as one newline-separated string, each line optionally
 * starting with an emoji. Entries are the form everything else works in: the
 * wire carries them, the widgets render them, and the editorial textarea is
 * formatted from them and parsed back on save. So the string is a storage
 * detail, and this file is the only place that knows the format — both sides of
 * the wire import it rather than agreeing separately about where an emoji ends
 * and its label begins.
 *
 * Parsing is not quite the inverse of formatting: a blank line is dropped, a
 * line is trimmed, and an emoji at the start of an entry's `text` is
 * indistinguishable from its `emoji` once stored. Callers that round-trip
 * through the stored form should expect it to normalise.
 */
const z = __importStar(require("zod"));
const keyutil_1 = require("./keyutil");
/** Max length for a user-authored (write-in) perspective label. */
exports.MAX_CUSTOM_PERSPECTIVE_LENGTH = 25;
/** Zero-width / format characters that can make a label look empty.
 * Excludes ZWJ (U+200D) which joins emoji. */
const INVISIBLE_CHARS = /[\u200B\u200C\u2060\uFEFF]/g;
/** True when the text contains at least one Unicode letter, combining mark, or
 * emoji. Extended_Pictographic alone matches text-default symbols like ™ and ©,
 * so we require VS16 (U+FE0F) after it to limit the match to characters that
 * actually render as emoji. For editor validation. */
const HAS_LETTER_OR_EMOJI = /(?:\p{Letter}|\p{Mark}|\p{Emoji_Presentation}|\p{Extended_Pictographic}\uFE0F)/u;
/** For user validation: must contain a letter or combining mark, no emoji. */
const HAS_LETTER = /(?:\p{Letter}|\p{Mark})/u;
const HAS_EMOJI = /(?:\p{Emoji_Presentation}|\p{Extended_Pictographic}\uFE0F)/u;
const emojiPattern = /^(\p{Regional_Indicator}\p{Regional_Indicator}|\p{Extended_Pictographic}|\p{Emoji_Presentation})(\u200D(\p{Regional_Indicator}\p{Regional_Indicator}|\p{Extended_Pictographic}|\p{Emoji_Presentation})|[\uFE00-\uFE0F])*/u;
/**
 * Splits a leading emoji off the front of a string. A multi-codepoint emoji
 * (ZWJ sequences like 👩‍👧‍👦, regional-indicator pairs like 🇯🇵, variation
 * selectors) counts as one emoji; an emoji anywhere but the front is left in
 * the text.
 */
function parseFirstEmojiFromString(inputStr) {
    const match = inputStr.match(emojiPattern);
    if (match) {
        const emoji = match[0];
        const text = inputStr.slice(emoji.length).trim();
        return { emoji, text };
    }
    else {
        return { emoji: null, text: inputStr.trim() };
    }
}
/** Parses the stored string into entries, in order. Blank lines are skipped. */
function parsePerspectiveLines(stored) {
    return (stored ?? '')
        .split('\n')
        .filter((line) => line.trim())
        .map((line) => {
        const { emoji, text } = parseFirstEmojiFromString(line);
        return { perspectiveKey: (0, keyutil_1.stringToFbKey)(text), emoji, text };
    });
}
/**
 * The label a perspective is tallied under: its emoji and text, recombined.
 *
 * The `comments.stats` perspective breakdown is keyed by this, so a reader
 * looking up an entry's tally has to build the key the same way the server
 * did — hence one definition, serving both a configured `Perspective` and a
 * comment's own `CommentPerspective`.
 */
function perspectiveVoteLabel({ emoji, text }) {
    return emoji ? `${emoji} ${text}` : text;
}
/**
 * Renders entries back to the stored form — one line each, emoji first. Also
 * how the editorial textarea is populated, so what an editor sees is exactly
 * what is stored.
 */
function formatPerspectiveLines(perspectives) {
    return perspectives.map(perspectiveVoteLabel).join('\n');
}
/**
 * Whether `text` begins with an emoji. Such a label cannot be stored as its
 * own field: it would read back with that emoji split off into `emoji`, so the
 * write refuses it rather than storing something that reads back differently.
 */
function hasLeadingEmoji(text) {
    return parseFirstEmojiFromString(text).emoji !== null;
}
/**
 * Strip invisible Unicode, NFC-normalize, and trim; returns the visible label
 * text.
 */
function sanitizePerspectiveLabel(text) {
    return text.normalize('NFC').replace(INVISIBLE_CHARS, '').trim();
}
/**
 * True when the label contains at least one Unicode letter or emoji.
 * Labels made entirely of digits, punctuation, or other symbols are rejected.
 */
function containsLetterOrEmoji(text) {
    return HAS_LETTER_OR_EMOJI.test(text);
}
/** True when the text contains any emoji character. */
function containsEmoji(text) {
    return HAS_EMOJI.test(text);
}
/**
 * Returns the reason a write-in label is invalid, or undefined when it passes.
 * Both the client (for immediate feedback) and the server (for storage
 * integrity) map this to their own message format, keeping the rule in one
 * place.
 */
function validateWriteInPerspectiveLabel(text) {
    const normalized = sanitizePerspectiveLabel(text);
    if (normalized.length === 0)
        return 'empty';
    if (HAS_EMOJI.test(normalized))
        return 'emoji';
    if (!HAS_LETTER.test(normalized))
        return 'noLetter';
    if (normalized.length > exports.MAX_CUSTOM_PERSPECTIVE_LENGTH)
        return 'tooLong';
    return undefined;
}
/**
 * The key a reader's own write-in perspective is stored under. Every other key
 * in a comment's perspective map is `stringToFbKey` of a configured
 * perspective's text.
 */
exports.WRITE_IN_PERSPECTIVE_KEY = 'writeIn';
/**
 * Bounds on the perspective tags one comment may carry. A space offers at most
 * `MAX_PERSPECTIVES`, and a comment may add one write-in on top.
 */
exports.MAX_COMMENT_PERSPECTIVES = 51;
/** Bound on a tag's label, as elsewhere in the API. */
exports.MAX_PERSPECTIVE_LABEL_LENGTH = 200;
/**
 * A perspective tag as a client sends it. A preset is a bare key, looked up in
 * the space's configured list server-side, so a client cannot choose the label
 * its tag renders under. A write-in is free text the server moderates before
 * storing anything.
 */
exports.commentPerspectiveInputSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('preset').describe('A perspective the space offers.'),
        perspectiveKey: z
            .string()
            .max(exports.MAX_PERSPECTIVE_LABEL_LENGTH)
            .describe("The configured perspective's key. The server resolves its label; a key the space does not configure is dropped."),
    }),
    z.object({
        kind: z.literal('writeIn').describe('A perspective the reader wrote themselves.'),
        text: z
            .string()
            .min(1)
            .max(exports.MAX_PERSPECTIVE_LABEL_LENGTH)
            .describe('The label the reader typed. Moderated before anything is stored. Blank once trimmed is a `BAD_REQUEST`.'),
    }),
]);
/** A perspective tag as a client reads it, labelled as the write stored it. */
exports.commentPerspectiveTagSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('preset').describe('A perspective the space offers.'),
        perspectiveKey: z.string().describe("The configured perspective's key, as the comment's tag is stored under."),
        text: z.string().describe('The label, as the space had it configured when the comment was posted.'),
        emoji: z.string().nullable().describe('The emoji shown beside the label, or null when there is none.'),
    }),
    z.object({
        kind: z.literal('writeIn').describe('A perspective the reader wrote themselves.'),
        text: z.string().describe('The label the reader wrote, after moderation accepted it.'),
    }),
]);
/**
 * Reduces the tags a draft is holding to what a write may send. A
 * preset keeps only its key, since the server looks the label up, and a
 * write-in keeps only its text.
 */
function perspectiveTagsToInput(tags) {
    return tags.map((tag) => tag.kind === 'preset' ? { kind: 'preset', perspectiveKey: tag.perspectiveKey } : { kind: 'writeIn', text: tag.text });
}
//# sourceMappingURL=perspectives.js.map